/** Presentation helpers for company Property import. No registry of canonical choices. */

import { DEAL_TYPE_LABELS, PROPERTY_TYPE_LABELS } from "./propertyLabels";
import { PROPERTY_STATUS_META } from "../../realty/lib/propertyLifecycle";
import {
  COMMERCIAL_SUITABILITY_LABELS,
  CONDITION_LABELS,
  FURNITURE_LABELS,
  HEATING_LABELS,
  LAND_PURPOSE_LABELS,
  WALL_LABELS,
} from "../../realty/lib/specLabels";

export const CLASSIFICATION_LABELS = {
  READY: "Готово",
  WARNING: "Готово з попередженням",
  ERROR: "Помилка — рядок не буде імпортовано",
  POSSIBLE_DUPLICATE: "Можливий дублікат — імпорт заблоковано",
};

export const OUTCOME_LABELS = {
  created: "Створено",
  blocked: "Заблоковано",
  existing_source: "Джерело вже існує — оновлення не було",
  failed: "Помилка рядка",
};

export const OPERATION_STATE_LABELS = {
  pending: "Перервано — імпорт не завершено",
  running: "Перервано — імпорт не завершено",
  completed: "Завершено",
  completed_with_errors: "Завершено з помилками",
  failed: "Збій операції",
};

export const DIAGNOSTIC_CATEGORY_LABELS = {
  parser: "Парсер",
  required_field: "Обов'язкове поле",
  enum: "Зіставлення значення",
  status_mapping: "Життєвий цикл",
  location: "Локація",
  subtype_validation: "Підтип / ділянка",
  numeric: "Число",
  responsible_member: "Відповідальний",
  duplicate: "Дублікат",
  core_validation: "Основні поля",
  content_safety: "Правила публікації",
};

export const MATCH_CODE_LABELS = {
  existing_cadastral_match: "збіг кадастрового номера",
  existing_location_shape_match: "збіг канонічної локації",
};

const TERMINAL_OPERATION_STATES = new Set([
  "completed",
  "completed_with_errors",
  "failed",
]);

export function classificationLabel(code) {
  return CLASSIFICATION_LABELS[code] || "Стан рядка";
}

export function outcomeLabel(code) {
  return OUTCOME_LABELS[code] || "Результат рядка";
}

export function operationStateLabel(state) {
  return OPERATION_STATE_LABELS[state] || "Стан операції";
}

export function isTerminalOperationState(state) {
  return TERMINAL_OPERATION_STATES.has(state);
}

export function isInterruptedOperation(state) {
  return state === "pending" || state === "running";
}

export function diagnosticCategoryLabel(category) {
  return DIAGNOSTIC_CATEGORY_LABELS[category] || "";
}

export function matchCodeLabel(code) {
  return MATCH_CODE_LABELS[code] || "Збіг";
}

export function matchCodeText(code) {
  return MATCH_CODE_LABELS[code] || "";
}

export function committableCounts(summary) {
  const ready = Number(summary?.ready) || 0;
  const warning = Number(summary?.warning) || 0;
  const error = Number(summary?.error) || 0;
  const possibleDuplicate = Number(summary?.possible_duplicate) || 0;
  return {
    attempted: ready + warning,
    blocked: error + possibleDuplicate,
  };
}

export function missingRequiredMappings(fields, mapping) {
  return (Array.isArray(fields) ? fields : [])
    .filter((field) => field && field.required_mapping)
    .filter((field) => !mapping || !mapping[field.key])
    .map((field) => field.key);
}

export function sheetSelectionRequired(inspect) {
  const sheets = Array.isArray(inspect?.sheets) ? inspect.sheets : [];
  return inspect?.format === "xlsx" && sheets.length > 1;
}

export function sourceNamespacePrompt(mapping) {
  return Boolean(mapping && mapping.external_id);
}

export function requirementsClaimBlocked(requirements) {
  return Boolean(requirements && requirements.truncated);
}

export function unresolvedHeaders(headers, mapping, extraUsed) {
  const used = new Set(
    Object.values(mapping || {}).filter((value) => typeof value === "string" && value),
  );
  (Array.isArray(extraUsed) ? extraUsed : []).forEach((header) => {
    if (typeof header === "string" && header) used.add(header);
  });
  return (Array.isArray(headers) ? headers : []).filter((header) => !used.has(header));
}

export const MEDIA_FAILURE_LABELS = {
  invalid_media_url: "Некоректне посилання",
  unsupported_media_scheme: "Схема посилання не підтримується",
  blocked_media_host: "Адресу заблоковано",
  media_dns_failed: "Не вдалося визначити адресу",
  media_connect_timeout: "Час з'єднання вичерпано",
  media_read_timeout: "Час читання вичерпано",
  media_redirect_rejected: "Перенаправлення відхилено",
  media_too_large: "Файл завеликий",
  media_http_error: "Сервер медіа відповів помилкою",
  media_invalid_image: "Файл не є коректним зображенням",
  media_capacity_exceeded: "Перевищено ліміт медіа",
  media_storage_failed: "Не вдалося зберегти файл",
  media_internal_error: "Внутрішня помилка медіа",
};

export function mediaFailureLabel(code) {
  return MEDIA_FAILURE_LABELS[code] || "";
}

export const INSPECT_WARNING_LABELS = {
  formula_ignored: "Формули у файлі проігноровано і не виконано",
  additional_sheets_ignored: "Інші аркуші не переглянуто, доки аркуш не обрано явно",
  duplicate_header: "У файлі повторено заголовок колонки",
  empty_sheet: "Аркуш не містить рядків даних",
  too_many_sheets: "У файлі більше аркушів, ніж дозволяє сервер",
  unknown_sheet: "Обраний аркуш не знайдено у файлі",
};

export const DIAGNOSTIC_CODE_LABELS = {
  formula_ignored: "Формули проігноровано і не виконано",
  required_field: "Обов'язкове значення порожнє",
  unresolved_enum: "Значення не має канонічного зіставлення",
  invalid_enum: "Значення не є канонічним варіантом",
  max_length: "Значення перевищує допустиму довжину",
  invalid_number: "Значення не є коректним числом",
  invalid_boolean: "Значення не є підтримуваним логічним варіантом",
  currency_defaulted: "Валюту не зіставлено. Використано типову USD",
  unresolved_status: "Статус джерела не зіставлено і не застосовано",
  status_recognized: "Статус джерела розпізнано. Попередній перегляд не змінює статус об'єкта",
  unresolved_responsible: "Відповідального не зіставлено з учасником компанії",
  invalid_responsible_user: "Відповідальний має бути активним учасником компанії",
  ambiguous_settlement: "Назва населеного пункту збігається з кількома записами",
  inactive_settlement: "Неактивний населений пункт не можна обрати",
  unresolved_settlement: "Населений пункт не знайдено",
  address_without_settlement: "Адресу не можна визначити без населеного пункту",
  ambiguous_address: "Адреса збігається з кількома записами",
  unresolved_address: "Адреса не збігається з канонічним записом",
  invalid_coordinate: "Координата некоректна",
  coordinates_not_identity: "Координати не є ідентичністю об'єкта",
  location_validation: "Локація не пройшла перевірку",
  duplicate_external_id: "Інший рядок файлу має той самий зовнішній ідентифікатор",
  duplicate_source_row: "Інший рядок файлу повторює ці дані",
  existing_source_identity: "Джерело вже існує",
  source_namespace_required: "Потрібен простір джерела",
  external_id_too_long: "Зовнішній ідентифікатор задовгий",
  existing_inventory_match: "Знайдено збіг в інвентарі компанії",
  lifecycle_rejected: "Життєвий цикл відхилив рядок",
  field_not_applicable: "Поле не застосовується до цього типу",
  named_object_mutex: "Іменований об'єкт не може поєднуватися з цими полями",
  subtype_validation: "Підтип або ділянка не пройшли перевірку",
  lifecycle_canonical_geography: "Канонічна географія не пройшла перевірку",
  media_capacity_exceeded: "Перевищено ліміт медіа",
  invalid_media_url: "Некоректне посилання на медіа",
  invalid_media_separator: "Роздільник медіа некоректний",
  content_policy_violation:
    "Текст порушує правила публікації. Змініть формулювання та спробуйте знову.",
};

export const API_ERROR_LABELS = {
  idempotency_conflict: "Конфлікт ключа ідемпотентності. Новий ключ автоматично не створюється.",
  company_import_forbidden: "Імпорт недоступний для цієї компанії.",
  invalid_idempotency_key: "Ключ ідемпотентності некоректний.",
  invalid_source_namespace: "Простір джерела некоректний.",
  invalid_mapping_shape: "Зіставлення має некоректну форму.",
  missing_required_mapping: "Не вистачає обов'язкового зіставлення.",
  unknown_source_column: "Колонку не знайдено у файлі.",
  duplicate_target: "Одну колонку зіставлено з кількома полями.",
  duplicate_media_column: "Колонку медіа обрано двічі.",
  invalid_media_separator: "Роздільник медіа некоректний.",
};

const FIELD_KIND_LABELS = {
  text: "текст",
  enum: "значення",
  decimal: "число",
  integer: "ціле число",
  boolean: "так/ні",
  responsible: "відповідальний",
};

export function fieldKindLabel(kind) {
  return FIELD_KIND_LABELS[kind] || "";
}

export function classificationTone(code) {
  if (code === "READY") return "ready";
  if (code === "WARNING") return "warning";
  if (code) return "error";
  return "neutral";
}

export function outcomeTone(code) {
  if (code === "created") return "ready";
  if (code === "existing_source") return "warning";
  if (code === "blocked" || code === "failed") return "error";
  return "neutral";
}

export function operationStateTone(state) {
  if (state === "completed") return "ready";
  if (state === "completed_with_errors" || state === "pending" || state === "running") {
    return "warning";
  }
  if (state === "failed") return "error";
  return "neutral";
}

export function diagnosticCodeLabel(code) {
  return DIAGNOSTIC_CODE_LABELS[code] || MEDIA_FAILURE_LABELS[code] || "";
}

export function inspectWarningText(warning) {
  const code = warning && warning.code ? String(warning.code) : "";
  const known = INSPECT_WARNING_LABELS[code] || "";
  return { primary: known || "Попередження файлу" };
}

export function diagnosticText(item) {
  const code = item && item.code ? String(item.code) : "";
  const known = diagnosticCodeLabel(code);
  const category = diagnosticCategoryLabel(item && item.category);
  if (known) {
    return { primary: category ? `${category}. ${known}` : known };
  }
  return { primary: "Не вдалося обробити значення" };
}

export function diagnosticCodeText(code) {
  const known = diagnosticCodeLabel(code);
  return { primary: known || "Не вдалося обробити значення" };
}

export function presentApiError(view) {
  const code = view && view.code ? String(view.code) : "";
  const known = API_ERROR_LABELS[code] || "";
  return { primary: known || "Запит не виконано." };
}

export function textError(primary, secondary = "") {
  return { primary, secondary };
}

const CURRENCY_LABELS = {
  UAH: "UAH",
  USD: "USD",
  EUR: "EUR",
};

const FIELD_ENUM_LABELS = {
  property_type: PROPERTY_TYPE_LABELS,
  deal_type: DEAL_TYPE_LABELS,
  currency: CURRENCY_LABELS,
  "residential.walls": WALL_LABELS,
  "commercial.walls": WALL_LABELS,
  "residential.heating": HEATING_LABELS,
  "commercial.heating": HEATING_LABELS,
  "residential.condition": CONDITION_LABELS,
  "commercial.condition": CONDITION_LABELS,
  "residential.furniture": FURNITURE_LABELS,
  "commercial.furniture": FURNITURE_LABELS,
  "land.purpose": LAND_PURPOSE_LABELS,
  "parcel.purpose_category": LAND_PURPOSE_LABELS,
  "commercial.suitable_for": COMMERCIAL_SUITABILITY_LABELS,
};

const GENERIC_ENUM_LABELS = [
  PROPERTY_TYPE_LABELS,
  DEAL_TYPE_LABELS,
  WALL_LABELS,
  HEATING_LABELS,
  CONDITION_LABELS,
  FURNITURE_LABELS,
  COMMERCIAL_SUITABILITY_LABELS,
  LAND_PURPOSE_LABELS,
  CURRENCY_LABELS,
];

function statusChoiceLabel(token) {
  const meta = PROPERTY_STATUS_META[token];
  return meta && meta.label ? meta.label : "";
}

export function canonicalChoiceText(value, fieldKey) {
  const token = value == null ? "" : String(value);
  if (!token) return "";
  if (fieldKey === "source_status") {
    return statusChoiceLabel(token) || "Канонічне значення";
  }
  const fieldMap = fieldKey ? FIELD_ENUM_LABELS[fieldKey] : null;
  if (fieldMap) return fieldMap[token] || "Канонічне значення";
  if (statusChoiceLabel(token)) return statusChoiceLabel(token);
  for (let index = 0; index < GENERIC_ENUM_LABELS.length; index += 1) {
    const label = GENERIC_ENUM_LABELS[index][token];
    if (label) return label;
  }
  return "Канонічне значення";
}

export const IMPORT_FIELD_LABELS = {
  external_id: "Зовнішній ідентифікатор",
  title: "Назва",
  property_type: "Тип",
  deal_type: "Угода",
  price: "Ціна",
  currency: "Валюта",
  description_public: "Публічний опис",
  description_private: "Приватний опис",
  source_status: "Статус",
  responsible_source: "Відповідальний",
  has_recovery_program: "Програма відновлення",
  has_housing_program: "Житлова програма",
  settlement: "Населений пункт",
  address_object: "Об'єкт адреси",
  manual_address_object: "Ручна адреса",
  building_number: "Номер будинку",
  local_area: "Локальний район",
  latitude: "Широта",
  longitude: "Довгота",
  "residential.total_area_m2": "Загальна площа житла",
  "residential.living_area_m2": "Житлова площа",
  "residential.kitchen_area_m2": "Площа кухні",
  "residential.rooms": "Кімнати",
  "residential.bathrooms": "Санвузли",
  "residential.floor": "Поверх",
  "residential.total_floors": "Поверхів",
  "residential.year_built": "Рік будівництва",
  "residential.land_area_m2": "Площа ділянки будинку",
  "residential.walls": "Стіни",
  "residential.heating": "Опалення",
  "residential.condition": "Стан",
  "residential.furniture": "Меблі",
  "residential.residential_complex": "Житловий комплекс",
  "residential.has_balcony": "Балкон",
  "residential.has_parking": "Паркінг",
  "land.area_m2": "Площа ділянки",
  "land.purpose": "Цільове призначення",
  "land.has_road_access": "Під'їзд",
  "land.has_utilities": "Комунікації",
  "parcel.cadastral_number": "Кадастровий номер",
  "parcel.purpose_category": "Категорія ділянки",
  "parcel.area_sotky": "Площа ділянки, сотки",
  "parcel.has_red_lines_or_restrictions": "Червоні лінії",
  "commercial.total_area_m2": "Загальна площа комерції",
  "commercial.floor": "Поверх комерції",
  "commercial.suitable_for": "Придатність",
  "commercial.walls": "Стіни комерції",
  "commercial.heating": "Опалення комерції",
  "commercial.condition": "Стан комерції",
  "commercial.furniture": "Меблі комерції",
};

function hasUkrainianCopy(value) {
  return /[А-Яа-яІіЇїЄєҐґ]/.test(String(value || ""));
}

export function importFieldLabel(field) {
  const key = field && field.key ? String(field.key) : "";
  if (key && IMPORT_FIELD_LABELS[key]) return IMPORT_FIELD_LABELS[key];
  const label = field && field.label ? String(field.label) : "";
  if (hasUkrainianCopy(label)) return label;
  return "Поле";
}

export function missingMappingText(fields, missingKeys) {
  const labels = (Array.isArray(missingKeys) ? missingKeys : []).map((key) => {
    const field = (Array.isArray(fields) ? fields : []).find((item) => item && item.key === key);
    return importFieldLabel(field || { key });
  });
  return `Потрібне зіставлення: ${labels.join(", ")}`;
}

export function formatLabel(format) {
  const value = format == null ? "" : String(format).toLowerCase();
  if (value === "csv") return "CSV";
  if (value === "xlsx") return "XLSX";
  return "Файл";
}

export function mediaKindLabel(kind) {
  if (kind === "image" || kind === "photo") return "Фото";
  if (kind === "plan") return "План";
  return kind || "Медіа";
}

export function buildMediaMapping({ imageColumns, planColumns, separator }) {
  const images = (Array.isArray(imageColumns) ? imageColumns : []).filter(
    (header) => typeof header === "string" && header,
  );
  const plans = (Array.isArray(planColumns) ? planColumns : []).filter(
    (header) => typeof header === "string" && header,
  );
  const payload = { images, plans };
  if (typeof separator === "string" && separator.trim()) {
    payload.separator = separator.trim();
  }
  return payload;
}

export function mediaStatusText(media) {
  const pending = Number(media && media.pending) || 0;
  const processing = Number(media && media.processing) || 0;
  const imported = Number(media && media.imported) || 0;
  const failed = Number(media && media.failed) || 0;
  const total = Number(media && media.total) || 0;
  if (pending + processing > 0) {
    return `Медіа обробляється. Усього: ${total}. Очікує: ${pending}. Обробляється: ${processing}. Імпортовано: ${imported}. Помилки: ${failed}.`;
  }
  return `Медіа завершено. Усього: ${total}. Імпортовано: ${imported}. Помилки: ${failed}.`;
}

export function mediaCountsText(media) {
  if (!media) return "Медіа не зіставлено";
  return `Фото: ${Number(media.image_count) || 0}. Плани: ${Number(media.plan_count) || 0}. Дублікати: ${Number(media.duplicate_count) || 0}. Некоректні: ${Number(media.invalid_count) || 0}.${media.over_capacity ? " Перевищено ліміт." : ""}`;
}

export function explicitMappingFromSuggestions(suggestions) {
  const mapping = {};
  (Array.isArray(suggestions) ? suggestions : []).forEach((item) => {
    if (!item || item.status !== "suggested" || !item.suggestion || !item.header) {
      return;
    }
    if (mapping[item.suggestion]) return;
    mapping[item.suggestion] = item.header;
  });
  return mapping;
}

export function activeMemberOptions(payload) {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.results)
      ? payload.results
      : [];
  return rows
    .filter((row) => row && row.status !== "suspended" && row.status !== "ended")
    .map((row) => {
      const user = row.user || null;
      const userId = user && user.id != null ? Number(user.id) : Number(row.user_id);
      return {
        userId,
        label: memberLabel(user, userId),
      };
    })
    .filter((row) => Number.isInteger(row.userId) && row.userId > 0);
}

function memberLabel(user, userId) {
  if (!user) return `Користувач #${userId}`;
  const fullname = typeof user.fullname === "string" ? user.fullname.trim() : "";
  if (fullname) return fullname;
  const handle = typeof user.handle === "string" ? user.handle.trim() : "";
  if (handle) return handle;
  return `Користувач #${userId}`;
}

function stableObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const sorted = {};
  Object.keys(value)
    .sort()
    .forEach((key) => {
      const item = value[key];
      if (item == null || item === "") return;
      sorted[key] = item;
    });
  return sorted;
}

function stableNested(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const sorted = {};
  Object.keys(value)
    .sort()
    .forEach((key) => {
      const child = stableObject(value[key]);
      if (Object.keys(child).length) sorted[key] = child;
    });
  return sorted;
}

export function importRequestFingerprint({
  fileToken,
  sheet,
  mapping,
  valueMappings,
  responsibleMapping,
  sourceNamespace,
  mediaMapping,
}) {
  const media = mediaMapping && typeof mediaMapping === "object" ? mediaMapping : {};
  return JSON.stringify({
    fileToken: fileToken || "",
    sheet: sheet || "",
    mapping: stableObject(mapping),
    valueMappings: stableNested(valueMappings),
    responsibleMapping: stableObject(responsibleMapping),
    sourceNamespace: typeof sourceNamespace === "string" ? sourceNamespace : "",
    mediaMapping: {
      images: Array.isArray(media.images) ? media.images : [],
      plans: Array.isArray(media.plans) ? media.plans : [],
      separator: typeof media.separator === "string" ? media.separator : "",
    },
  });
}

export function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `import-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function resolveCommitKey({ previous, fingerprint, createKey }) {
  if (
    previous &&
    previous.key &&
    previous.fingerprint === fingerprint
  ) {
    return previous;
  }
  const nextKey = typeof createKey === "function" ? createKey() : createIdempotencyKey();
  return { fingerprint, key: nextKey };
}

export function readOperationId(search) {
  const params = new URLSearchParams(
    typeof search === "string" ? search : "",
  );
  const raw = params.get("operation");
  if (!raw || !/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1) return null;
  return value;
}

export async function fileContentToken(file) {
  if (!file) return "";
  let bytes = new Uint8Array();
  if (typeof file.arrayBuffer === "function") {
    const buffer = await file.arrayBuffer();
    bytes = new Uint8Array(buffer);
  }
  if (!bytes.length && typeof file.text === "function") {
    const encoded = new TextEncoder().encode(await file.text());
    bytes = encoded;
  }
  let hash = 5381;
  for (let index = 0; index < bytes.length; index += 1) {
    hash = ((hash * 33) ^ bytes[index]) >>> 0;
  }
  return `${file.size}:${bytes.length}:${hash}`;
}

export function allowedValuesFor(fields, key) {
  const field = (Array.isArray(fields) ? fields : []).find((item) => item.key === key);
  if (!field || !Array.isArray(field.allowed_values)) return [];
  return field.allowed_values;
}
